/**
 * Vendor portal twin of admin/authApi.ts. See that file for design notes.
 * Defect coverage: INT-001, INT-002, INT-018 for the vendor app.
 */

import { apiRequest, ApiError, setTenantCode, getTenantCode } from '../api/client.js';
import type { AuthUser } from './AuthProvider.js';

export { setTenantCode, getTenantCode };

export interface NormalizedLogin {
  readonly requiresMfa: boolean;
  readonly mfaChallengeId: string | null;
  readonly mfaMethod: string | null;
  readonly token: string | null;
  readonly refreshToken: string | null;
  readonly user: AuthUser | null;
}

export interface NormalizedVerify {
  readonly token: string;
  readonly refreshToken: string | null;
  readonly user: AuthUser;
}

interface BackendTokenData {
  readonly accessToken: string;
  readonly refreshToken?: string;
  readonly tokenType?: string;
  readonly expiresIn?: number;
  readonly sessionId?: string;
  readonly userId?: string;
  readonly user?: AuthUser;
}

interface BackendLoginResponse {
  readonly success?: boolean;
  readonly mfa_required?: boolean;
  readonly mfa_token?: string;
  readonly mfa_method?: string;
  readonly expires_in?: number;
  readonly data?: BackendTokenData;
}

interface LegacyLoginResponse {
  readonly requiresMfa?: boolean;
  readonly mfaChallengeId?: string;
  readonly token?: string;
  readonly user?: AuthUser;
}

type LoginWire = BackendLoginResponse & LegacyLoginResponse;

interface BackendVerifyResponse {
  readonly success?: boolean;
  readonly data?: BackendTokenData;
}

interface LegacyVerifyResponse {
  readonly token?: string;
  readonly user?: AuthUser;
}

type VerifyWire = BackendVerifyResponse & LegacyVerifyResponse;

function userFromTokenData(data: BackendTokenData, fallbackUsername: string): AuthUser {
  if (data.user) return data.user;
  const id = data.userId ?? `user-${Date.now()}`;
  return {
    id,
    username: fallbackUsername,
    displayName: fallbackUsername,
    roles: [],
  };
}

export async function login(username: string, password: string, tenantCode?: string): Promise<NormalizedLogin> {
  if (tenantCode !== undefined) setTenantCode(tenantCode);
  // Body matches LoginPasswordRequestDto exactly. Backend uses
  // StrictValidationPipe (whitelist=true, forbidNonWhitelisted=true) so
  // extra fields would 400. The tenant travels as a header, not in the body.
  const res = await apiRequest<LoginWire>('/auth/login', {
    method: 'POST',
    body: {
      identifier: username,
      password,
      deviceType: 'WEB',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
    },
  });

  if (res.mfa_required === true) {
    return {
      requiresMfa: true,
      mfaChallengeId: res.mfa_token ?? null,
      mfaMethod: res.mfa_method ?? null,
      token: null,
      refreshToken: null,
      user: null,
    };
  }
  if (res.requiresMfa === true) {
    return {
      requiresMfa: true,
      mfaChallengeId: res.mfaChallengeId ?? null,
      mfaMethod: null,
      token: null,
      refreshToken: null,
      user: null,
    };
  }
  if (res.data?.accessToken) {
    return {
      requiresMfa: false,
      mfaChallengeId: null,
      mfaMethod: null,
      token: res.data.accessToken,
      refreshToken: res.data.refreshToken ?? null,
      user: userFromTokenData(res.data, username),
    };
  }
  return {
    requiresMfa: false,
    mfaChallengeId: null,
    mfaMethod: null,
    token: res.token ?? null,
    refreshToken: null,
    user: res.user ?? null,
  };
}

export async function verifyMfa(
  mfaToken: string,
  code: string,
  method: string | null,
): Promise<NormalizedVerify> {
  // Body matches VerifyMfaDto exactly (forbidNonWhitelisted).
  const body = {
    mfaToken,
    mfaCode: code,
    mfaMethod: method ?? 'TOTP',
  };

  let res: VerifyWire;
  try {
    res = await apiRequest<VerifyWire>('/auth/verify-mfa', { method: 'POST', body });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      res = await apiRequest<VerifyWire>('/auth/mfa/verify', { method: 'POST', body });
    } else {
      throw err;
    }
  }

  if (res.data?.accessToken) {
    return {
      token: res.data.accessToken,
      refreshToken: res.data.refreshToken ?? null,
      user: userFromTokenData(res.data, 'user'),
    };
  }
  if (res.token && res.user) {
    return { token: res.token, refreshToken: null, user: res.user };
  }
  throw new ApiError(500, res, 'MFA verify: unexpected response shape');
}

export async function refresh(): Promise<NormalizedVerify | null> {
  // SEC-001: refresh token comes from the httpOnly cookie attached by
  // the browser when api/client.ts uses credentials:'include'.
  try {
    const res = await apiRequest<BackendVerifyResponse>('/auth/refresh', {
      method: 'POST',
      body: {},
    });
    if (res.data?.accessToken) {
      return {
        token: res.data.accessToken,
        refreshToken: null,
        user: userFromTokenData(res.data, 'user'),
      };
    }
    return null;
  } catch {
    return null;
  }
}

export async function forgot(username: string): Promise<{ resetTokenIssued: true }> {
  const body = { identifier: username, tenantId: 'default' };
  try {
    await apiRequest<unknown>('/auth/forgot-password', { method: 'POST', body });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      await apiRequest<unknown>('/auth/forgot', { method: 'POST', body });
    } else {
      throw err;
    }
  }
  return { resetTokenIssued: true } as const;
}

export async function logout(): Promise<void> {
  try {
    await apiRequest<unknown>('/auth/logout', { method: 'POST' });
  } catch {
    /* ignore */
  }
}
